import { useEffect } from 'react';
import NextImage from 'next/legacy/image';
import { CacheCaseStudy, CacheCaseStudyContent } from '@sy/ssr-types';
import analytics from '@sy/app-client/src/ssrAdapter/services/analytics';
import {
	MarketingHeader,
	MarketingHighlight,
	MarketingSignup,
	MarketingUsp,
} from '@sy/app-client/src/ssrAdapter/components/Marketing';

import type { MarketingHeaderProps, MarketingHighlightProps } from './types';
import CaseStudyHook from '../../components/CaseStudyHook';
import { CaseStudyHookProps } from '../../components/CaseStudyHook/CaseStudyHook';
import CaseStudyConclusion, {
	CaseStudyConclusionProps,
} from '../../components/CaseStudyConclusion/CaseStudyConclusion';
import CaseStudyMainContent, {
	CaseStudyMainContentProps,
} from '../../components/CaseStudyMainContent/CaseStudyMainContent';
import CaseStudyInfoCard, {
	CaseStudyInfoCardProps,
} from '../../components/CaseStudyInfoCard/CaseStudyInfoCard';
import CaseStudyQuote, {
	CaseStudyQuoteProps,
} from '../../components/CaseStudyQuote/CaseStudyQuote';

const contentMapper = ({
	content,
	tags,
	index,
}: {
	content: CacheCaseStudyContent;
	tags?: string[];
	index: number;
}) => {
	switch (content.type) {
		case 'landingPageHeader': {
			const props: MarketingHeaderProps = {
				title: content.title,
			};
			if (content.showAuthForm) {
				props.cta = <MarketingSignup />;
			}
			if (content.subtitle) {
				props.subtitles = [content.subtitle];
			}
			return <MarketingHeader key={`${content.type}-${index}`} {...props} />;
		}

		case 'landingPageHighlight': {
			const props: MarketingHighlightProps = {
				title: content.title,
				body: content.subtitle,
				cta: {
					url: content.linkUrl,
					title: content.linkTitle,
				},
				darkBackground: true,
			};

			return <MarketingHighlight key={`${content.type}-${index}`} {...props} />;
		}

		case 'quote': {
			const props: CaseStudyQuoteProps = {
				quote: content.quote,
				name: content.name,
				jobTitle: content.jobTitle,
				copy: content.copy,
				image: content.image,
				backgroundColor: content.backgroundColor,
			};

			return <CaseStudyQuote key={`${content.type}-${index}`} {...props} />;
		}

		case 'caseStudyHook': {
			const props: CaseStudyHookProps = {
				content: content.content,
				image: content.image,
				logo: content.logo,
				tags,
				videoUrl: content.videoUrl,
			};

			return <CaseStudyHook key={`${content.type}-${index}`} {...props} />;
		}

		case 'caseStudyMainContent': {
			const props: CaseStudyMainContentProps = {
				title: content.title,
				content: content.content,
				shape: content.shape,
				color: content.color,
				contentPosition: content.contentPosition,
			};

			return (
				<CaseStudyMainContent key={`${content.type}-${index}`} {...props} />
			);
		}

		case 'caseStudyInfoCard': {
			const props: CaseStudyInfoCardProps = {
				title1: content.title1,
				title2: content.title2,
				title3: content.title3,
				description1: content.description1,
				description2: content.description2,
				description3: content.description3,
				highlight1: content.highlight1,
				highlight2: content.highlight2,
				highlight3: content.highlight3,
			};

			return <CaseStudyInfoCard key={`${content.type}-${index}`} {...props} />;
		}

		case 'caseStudyConclusion': {
			const props: CaseStudyConclusionProps = {
				title: content.title,
				text: content.text,
			};

			return (
				<CaseStudyConclusion key={`${content.type}-${index}`} {...props} />
			);
		}

		case 'usp': {
			const props = {
				title: content.title,
				backgroundColor: content.backgroundColor,
				cards: content.cards,
			};

			return (
				<MarketingUsp
					key={`${content.type}-${index}`}
					NextImage={NextImage}
					{...props}
				/>
			);
		}
		default:
			return null;
	}
};

const mapper = ({
	content,
	tags,
}: {
	content?: CacheCaseStudyContent[];
	tags?: string[];
}) =>
	content?.map((eachContent, index) =>
		contentMapper({ content: eachContent, index, tags }),
	);

const CaseStudyPage = ({ data }: Props) => {
	useEffect(() => {
		analytics.page('Content', 'Case Study', { contentType: 'Case Study' });
	}, []);

	return <>{mapper({ content: data.content, tags: data.tags })}</>;
};

type Props = {
	data: CacheCaseStudy;
};

export default CaseStudyPage;
